import { Box, ButtonBase } from "@mui/material";
import React from "react";

export interface SectionStripEntry {
  id: string;
  // The name of the section, e.g. "Claims"; its count is in its heading
  label: string;
}

export interface SectionStripProps {
  sections: SectionStripEntry[];
  // The id of the section in view
  current: string | null;
  // Asks for a section to be brought into view
  onSelect: (id: string) => void;
}

// Height (px) of the strip; what jumps to a section leaves this much room
export const SECTION_STRIP_HEIGHT = 40;

/**
 * The names of the sections of a view in a row. The section in view is
 * marked; a click asks for a section.
 */
const SectionStrip: React.FC<SectionStripProps> = ({
  sections,
  current,
  onSelect,
}) => (
  <Box
    component="nav"
    aria-label="Sections"
    sx={{
      display: "flex",
      alignItems: "stretch",
      gap: 0.5,
      height: SECTION_STRIP_HEIGHT,
      px: 2,
      overflowX: "auto",
      backgroundColor: "background.paper",
      borderBottom: 1,
      borderColor: "divider",
    }}
  >
    {sections.map((section) => {
      const inView = section.id === current;
      return (
        <ButtonBase
          key={section.id}
          aria-current={inView ? "true" : undefined}
          onClick={() => onSelect(section.id)}
          sx={{
            px: 1,
            gap: 0.75,
            flexShrink: 0,
            // The app's face, not the browser's button font
            fontFamily: "inherit",
            fontSize: "0.78rem",
            fontWeight: 500,
            color: inView ? "text.primary" : "text.secondary",
            borderBottom: 2,
            borderColor: inView ? "link.main" : "transparent",
            "&:hover": { color: "text.primary" },
          }}
        >
          {section.label}
        </ButtonBase>
      );
    })}
  </Box>
);

export default SectionStrip;
